import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CartPage from '../features/cart/CartPage';
import ProfilePage from '../features/profile/ProfilePage';
import ProductList from '../features/product/ProductList';
import ProductPage from '../features/product/ProductPage';
import ServicePage from '../features/product/ServicePage';
import ServiceCatalogPage from '../features/product/ServiceCatalogPage';
import OrdersPage from '../features/orders/OrdersPage';
import OrderSuccessPage from '../features/orders/OrderSuccessPage';
import CatalogPage from '../features/product/CatalogPage';
import PlayStationStorePage from '../features/product/PlayStationStorePage';
import GameRequestPage from '../features/product/GameRequestPage';
import CategoryPage from '../features/product/CategoryPage';
import AdminPanelPage from '../features/admin/AdminPanelPage';
import AdminProductsPage from '../features/admin/AdminProductsPage';
import AdminEditProductPage from '../features/admin/AdminEditProductPage';
import AdminServicesPage from '../features/admin/AdminServicesPage';
import AdminSubscriptionsPage from '../features/admin/AdminSubscriptionsPage';
import AdminUsersPage from '../features/admin/AdminUsersPage';
import AdminBannersPage from '../features/admin/AdminBannersPage';
import AdminCategoriesPage from '../features/admin/AdminCategoriesPage';
import AdminPlatformCardsPage from '../features/admin/AdminPlatformCardsPage';
import AdminTopUpCardsPage from '../features/admin/AdminTopUpCardsPage';
import { TopUpCardPage, TopUpCardsPage } from '../features/product/TopUpCardsPage';
import FavoritesPage from '../features/favorites/FavoritesPage';
import LoginPage from '../features/auth/LoginPage';
import RegisterPage from '../features/auth/RegisterPage';
import AboutPage from '../features/static/AboutPage';
import SupportPage from '../features/static/SupportPage';
import ContactsPage from '../features/static/ContactsPage';
import ReviewsPage from '../features/static/ReviewsPage';
import TermsPage from '../features/static/TermsPage';
import PrivacyPage from '../features/static/PrivacyPage';
import { authService } from '../features/auth/authService';
import { isMiniApp } from '../telegram';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  if (!authService.isAuthenticated()) {
    // No login page inside Telegram — auth runs automatically on launch. If we
    // still land here unauthenticated, send home instead of a dead /login.
    if (isMiniApp()) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
};

export const AppRoutes = () => {
  return (
    <Routes>
      {/* In the mini-app there is no manual login/registration — auto-auth handles
          it — so these routes just bounce home instead of showing dead forms. */}
      <Route path="/login" element={isMiniApp() ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={isMiniApp() ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/" element={<ProductList />} />
      <Route path="/catalog" element={<CatalogPage />} />
      <Route path="/playstation-store" element={<PlayStationStorePage />} />
      <Route path="/games/request" element={<GameRequestPage />} />
      <Route path="/category/:slug" element={<CategoryPage />} />
      <Route path="/product/:id" element={<ProductPage />} />
      <Route path="/service/:id" element={<ServicePage />} />
      <Route path="/services/:category" element={<ServiceCatalogPage />} />
      <Route path="/top-up-cards" element={<TopUpCardsPage />} />
      <Route path="/top-up-card/:id" element={<TopUpCardPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/order-success" element={<RequireAuth><OrderSuccessPage /></RequireAuth>} />
      <Route path="/favorites" element={<RequireAuth><FavoritesPage /></RequireAuth>} />
      <Route path="/orders" element={<RequireAuth><OrdersPage /></RequireAuth>} />
      <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/contacts" element={<ContactsPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/admin" element={<RequireAuth><AdminPanelPage /></RequireAuth>}>
        <Route index element={<Navigate to="products" replace />} />
        <Route path="products" element={<AdminProductsPage />} />
        <Route path="products/:id/edit" element={<AdminEditProductPage />} />
        <Route path="services" element={<AdminServicesPage />} />
        <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="banners" element={<AdminBannersPage />} />
        <Route path="categories" element={<AdminCategoriesPage />} />
        <Route path="platforms" element={<AdminPlatformCardsPage />} />
        <Route path="top-up-cards" element={<AdminTopUpCardsPage />} />
      </Route>
    </Routes>
  );
}; 
